import { CountrySettings } from 'api/general-countries';
import { UserInfo } from 'model/business-base';
import { ApproveType, CountryApprovalRequirement } from 'api/swagger/ApiClient'
import { ApprovalLevel, Country, OrganisationEntity, TravelType } from 'model/business-keywords';
import { UserContact } from 'model/business-types';
import { aprovalRequired } from 'services/safety-service';
import { UploadedFile } from 'model/essential-types';
import { TravelSegment } from './trip-details';

export interface TravelRequest {
  name: string | null;
  travelers: BusinessTraveler[];
  segments: TravelSegment[];
  businessDetails: BusinessDetails;
  countryRequests: CountryRequest[];
  checklistItems: ChecklistItem[];
  attachments: UploadedFile[];
}

export interface CorporateTraveler {
  kind: 'corporate-traveler';
  user: UserContact | null;
  countryExperiences: CountryExperience[];
}

export interface ContractorTraveler {
  kind: 'contractor-traveler';
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  mail: string | null;
  lineManager: UserContact | null;
  organisationEntity: OrganisationEntity | null;
  countryExperiences: CountryExperience[];
}

export interface CountryExperience {
  country: Country;
  revisit: boolean | null;
}

export interface BusinessDetails {
  travelType: TravelType | null;
  reasonForTravel: string | null;
  localArrangements: string | null;
  businessContacts: BusinessContact[];
}

export interface BusinessContact {
  name: string | null;
  phone: string | null;
  mail: string | null;
}

export interface CountryRequest {
  country: Country;
  transit: boolean;
  conditionMessage: string | null;
  approvalLevel: ApprovalLevel;
  approvalRequirementValue: CountryApprovalRequirement;
  approval: CountryApproval | null;
}

export interface CountryApproval {
  approvalTypeValue: ApproveType;
  approvers: CountryApprover[];
}

export interface CountryApprover {
  approver: UserInfo;
}

export interface ChecklistItem {
  message: string;
  checked: boolean;
  mandatory: boolean;
}

export interface CountryVisit {
  country: Country | null;
  transit: boolean;
}

export type BusinessTraveler = CorporateTraveler | ContractorTraveler;

export function initTravelRequest(data?: Partial<TravelRequest>): TravelRequest {
  return { name: null, travelers: [], segments: [], businessDetails: initBusinessDetails(), countryRequests: [], checklistItems: [], attachments: [], ...data };
}

export function initCorporateTraveler(data?: Partial<CorporateTraveler>): CorporateTraveler {
  return { kind: 'corporate-traveler', user: null, countryExperiences: [], ...data };
}

export function initContractorTraveler(data?: Partial<ContractorTraveler>): ContractorTraveler {
  return { kind: 'contractor-traveler', firstName: null, lastName: null, phone: null, mail: null, organisationEntity: null, lineManager: null, countryExperiences: [], ...data };
}

export function initCountryExperience(data: Omit<CountryExperience, 'revisit'> & Partial<CountryExperience>): CountryExperience {
  return { country: data.country, revisit: null, ...data };
}

export function initBusinessDetails(data?: Partial<BusinessDetails>): BusinessDetails {
  return { travelType: null, reasonForTravel: null, localArrangements: null, businessContacts: [], ...data };
}

export function initBusinessContact(data?: Partial<BusinessContact>): BusinessContact {
  return { name: null, phone: null, mail: null, ...data };
}

export function initCountryRequest(data: Omit<CountryRequest, 'approval'> & Partial<CountryRequest>): CountryRequest {
  return { country: data.country, transit: data.transit, conditionMessage: data.conditionMessage, approvalLevel: data.approvalLevel, approvalRequirementValue: data.approvalRequirementValue, approval: null, ...data };
}

export function initCountryVisit(data?: Partial<CountryVisit>): CountryVisit {
  return { country: null, transit: false, ...data };
}

export function produceCountryRequest(country: Country, settings: CountrySettings, transit: boolean, approver?: UserInfo): CountryRequest {
  return initCountryRequest({ country: country, transit: transit, conditionMessage: settings.conditionMessage, approvalLevel: settings.approvalLevel, approvalRequirementValue: settings.approvalRequirementValue, approval: aprovalRequired(settings.approvalRequirementValue, transit) ? produceCountryApproval(settings, approver) : null });
}

export function produceCountryApproval(settings: CountrySettings, approver?: UserInfo): CountryApproval {
  return { approvalTypeValue: settings.approvalTypeValue, approvers: settings.approvalTypeValue !== ApproveType.One || settings.approvers.length === 1 && (approver == null || settings.approvers[0].id !== approver.id) ? settings.approvers.map(u => ({ approver: u })) : [{ approver: approver != null ? settings.approvers.find(u => u.id === approver.id) : null }].filter(a => a.approver != null) };
}

export function extractApprovableCountries(countryRequests: CountryRequest[]) {
  return countryRequests.filter(r => aprovalRequired(r.approvalRequirementValue, r.transit)).map(r => r.country);
}
